import {useEffect} from 'react';
import Head from 'next/head';
import {useRouter} from 'next/router';
import {useIntl} from 'react-intl';
import {Button, Center, Flex, Image, Text, Container} from '@chakra-ui/react';

import {py} from '@eksab/theme';
import {useTranslate} from '@eksab/i18n';
import {getLanguage} from '@eksab/util';

import {MoreTriviaGames, PlayHeader} from './';
import {useTriviaStore} from '../state';
import alarm from '../icons/alarm.png';
import outOfTime from '../icons/out-of-time.webp';
import {ArrowIcon} from '../icons';
import {useEmbedded} from '@eksab/hooks/useEmbedded';

export function OutOfTime() {
  const {locale} = useRouter();
  const format = useIntl().formatNumber;

  const t = useTranslate();
  const challenge = useTriviaStore((state) => state.challenge);
  const topic = challenge?.topic! ? 'art' : challenge?.topic!;
  const question = useTriviaStore((state) => state.currentQuestion)!;
  const currentScore = useTriviaStore((state) => state.currentScore);
  const noOfQuestions = useTriviaStore((state) => state.challenge?.no_of_questions!);
  const viewScore = useTriviaStore((state) => state.viewScore);

  const outOfTimeImage = challenge?.topic === 'art' ? outOfTime : alarm;

  const {isEmbedded} = useEmbedded();

  useEffect(() => {
    const outOfTimeSound = new Audio('/sounds/out-of-time.wav');
    outOfTimeSound.volume = 0.4;
    outOfTimeSound.play();

    mixpanel?.track('Out of Time', {
      question: question.body,
      score: currentScore,
      noOfQuestions,
    });
  }, [currentScore, noOfQuestions, question.body]);

  return (
    <Flex flexDir="column" flex="1">
      <Head>
        <title>{`${t(`trivia.${topic}.out-of-time`)} - ${t('app.trivia')} | ${t('app.name')}`}</title>
      </Head>

      <PlayHeader />

      <Container
        flex={1}
        w="fit-content"
        py={py}
        display="grid"
        gridTemplateColumns={{base: '1fr', md: '1fr 1fr'}}
        alignItems="center"
        alignContent={{base: 'start', md: 'center'}}
        justifyItems="center"
        rowGap={{base: 3, md: 2}}
        columnGap="14"
      >
        <Center gridRow={{base: '2/3', md: '1/6'}} maxW={{base: '85%', md: 96}}>
          <Image src={outOfTimeImage.src} alt="" bg="white" boxSize="full" />
        </Center>

        <Text as="h1" fontSize={{base: '2xl', lg: '2rem'}} fontWeight="700" color="#EC1958">
          {t(`trivia.${topic}.out-of-time`)}
        </Text>

        <Text fontWeight="600" color="#6c6c6c" textAlign="center" maxW="24rem" w="85%">
          {t(`trivia.${topic}.out-of-time.description`)}
        </Text>

        {challenge?.termination_mode == 'stop_on_wrong_answer' && (
          <Flex
            align="center"
            gap="3.5"
            w={{base: 'full', md: 'fit-content'}}
            bg="#f3f3f3"
            my={{md: 6}}
            py="4"
            px="6"
            rounded="1.25rem"
          >
            {question.type === 'image' && (
              <Image
                src={question.image}
                alt=""
                flexShrink="0"
                boxSize={{base: 12, md: 16}}
                rounded="full"
                objectFit="cover"
              />
            )}

            <Flex flexDir="column" gap="1">
              <Text display="flex" gap="1" alignItems="center" fontSize={{base: 'md', md: 'xl'}} fontWeight="700">
                {format(currentScore)}

                <Text as="span" fontSize="x-small" fontWeight="400" color="#9B9B9B">
                  /
                </Text>

                <Text as="span" color="#9b9b9b" fontSize={{base: 'xs', md: 'inherit'}}>
                  {format(noOfQuestions)}
                </Text>
              </Text>

              <Text userSelect="none" fontWeight="600" fontSize={{base: 'xs', md: 'md'}} maxW="80">
                {question.body}
              </Text>
            </Flex>
          </Flex>
        )}

        <Button minH="14" gap="16" justifyContent="space-between" onClick={viewScore}>
          <Text as="span" color="inherit" fontSize={{base: 'md', md: 'lg'}}>
            {t('trivia.view-score')}
          </Text>

          <ArrowIcon transform="auto" scaleX={getLanguage(locale) === 'en' ? 1 : -1} />
        </Button>
      </Container>

      {!isEmbedded && <MoreTriviaGames />}
    </Flex>
  );
}
